import { Box, Container } from "@mui/material";
import { MprcContainer, MprcRequestIndicator } from "components";
import { ButtonSave } from "components/buttons";
import { MprcInfiniteLoading } from "components/MprcInfiniteLoading";
import { MprcRequestErrorDialog } from "components/MprcRequestErrorDialog";
import { MprcResolveRequestErrors } from "components/MprcRequestErrorReport";
import { FieldValueItem, RecordInfoDto, UpdateProfilePayload, UserEditProfileResponse, UserPhoneTypeSelectorResponse, UserType } from "generated/CoreApi_v1";
import { usePageTitle } from "hooks/usePageTitle";
import LibUtils from "lib/LibUtils";
import { useSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import { useSession } from "providers/SessionProvider";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { EditUserSecondaryBox } from "views/foundation/user/Home/EditProfile/EditUserSecondaryBox";
import { EditUserHeader } from "./EditUserHeader";
import { EditUserMainBox } from "./EditUserMainBox";

////////////////////////////////////////////////////////////////////////////

export interface RecordInfoUser extends RecordInfoDto {
    lastLoginOn?: Date;
}

interface IBoxValidations {
    mainBox: boolean;
    tabsBox: boolean;
}

////////////////////////////////////////////////////////////////////////////

function getEmptyDto(): Partial<UserEditProfileResponse> {
    return {
        userId: undefined,
        name: undefined,
        displayName: undefined,
        email: undefined,
        isExternalUser: false,
        thumbnail: undefined,
        preferredLocale: undefined,
        workplace: undefined,
        workplaceText: undefined,
        lastLoginOn: undefined,
        status: undefined,
        recordInfo: undefined,
        admissionDate: undefined,
        dismissedDate: undefined,
        phones: []
    };
}

function getEmptyValidations(): IBoxValidations {
    return { mainBox: false, tabsBox: false };
}

////////////////////////////////////////////////////////////////////////////

interface IProps {
    userId: string;
}

/**
 * Edição de Usuários
 * @param props
 */
export const EditUserProfile: React.FC<IProps> = (props) => {

    const api = useCoreApi();
    const navigate = useNavigate();
    const session = useSession();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();

    const [loading, setLoading] = useState<boolean>(true);
    const [saving, setSaving] = useState<boolean>(false);
    const [viewError, setViewError] = useState<any>();
    const [viewData, setViewData] = useState<Partial<UserEditProfileResponse>>(() => getEmptyDto());
    const [viewFieldsErrors, setViewFieldsErrors] = useState<ApiProblemErrors>();
    const [loadDto, setLoadDto] = useState<UserEditProfileResponse>();
    const [recordChanged, setRecordChanged] = useState<boolean>(false);
    const [reloadOnClose, setReloadOnClose] = useState<boolean>(false);
    const [subForm, setSubForm] = useState<any>();
    const [boxValidations, setBoxValidations] = useState<IBoxValidations>(getEmptyValidations());

    const phoneTypes = useRef<UserPhoneTypeSelectorResponse[] | undefined>(undefined);
    const canSave = recordChanged && Object.values(boxValidations).filter((x) => x === false).length == 0 && !loading;

    usePageTitle(t("knowledge:Feed.UserProfile.PageTitle"));

    ////////////////////////////////////////////////////////////////////////////

    ///
    const getSaveCommand = (): UpdateProfilePayload => {
        let allFields = (viewData.customFieldsBasic ?? [])
            .concat(viewData.customFieldsCorporate ?? [])
            .concat(viewData.customFieldsPersonal ?? [])
            .concat(viewData.customFieldsAdditional ?? [])
            .concat(viewData.customFieldsConcealed?.filter((x) => x.fieldValue !== undefined) ?? []);

        let customFields = allFields.map((x) => ({ fieldId: x.fieldId, fieldValue: x.fieldValue } as FieldValueItem));

        let command: UpdateProfilePayload = {
            userId: viewData.userId!,
            name: viewData.name!,
            displayName: viewData.displayName!,
            email: viewData.email ?? null,
            preferredLocale: viewData.preferredLocale ?? null,
            workplaceId: viewData.settings!.isWorkplaceFreeText === false ? viewData.workplace?.workplaceId ?? null : null,
            workplaceText: viewData.settings!.isWorkplaceFreeText === true ? viewData.workplaceText ?? null : null,
            thumbnailId: viewData.thumbnail?.fileId ?? null,
            userType: viewData.isExternalUser ? UserType.External : UserType.Internal,
            allowUserConnections: viewData.allowUserConnections ?? false,
            receiveDailySummaryEmail: viewData.receiveDailySummaryEmail ?? true,
            discloseBirthDate: viewData.discloseBirthDate ?? false,
            admissionDate: viewData.admissionDate ?? null,
            dismissedDate: viewData.dismissedDate ?? null,
            customFields: customFields,
            phones: viewData.phones ? viewData.phones.filter((x) => x.phoneNumber) : null,
            socialNetworks: viewData.socialNetworks
                ? viewData.socialNetworks.map((x) => ({ socialNetworkId: x.socialNetworkId, socialNetworkType: x.socialNetworkType }))
                : null,
            accessExpiresOn: viewData.accessExpiresOn ?? null,
            birthDate: viewData.birthDate ?? null
        };

        return command;
    };

    ///
    const handleSave = () => {
        if (canSave) setSaving(true);
    };

    ///
    const refreshData = (dto: UserEditProfileResponse) => {
        setLoadDto(LibUtils.deepCopy(dto));
        setViewData(dto);
        setViewError(undefined);
        setViewFieldsErrors(undefined);
    };

    ///
    const resolveErrors = (e: any | unknown) => {
        var r = MprcResolveRequestErrors(e);
        setViewFieldsErrors(r.fieldsWithError);
        setViewError(r.otherErrors);
        if (r.fieldsWithError != null) enqueueSnackbar(t("Record.SaveFailedWithFieldErrorsMessage", { ns: "shared" }), { variant: "error" });
    };

    ///
    function handleUpdateViewData(newDto: UserEditProfileResponse) {
        setViewData(newDto);
    }

    ///
    const getHeaderItems = () => {
        const formHeaderOptions: React.ReactNode[] = [];

        formHeaderOptions.push(<ButtonSave key="btnSave" rounded size="large" variant="text" onClick={handleSave} disabled={!canSave} />);

        return formHeaderOptions;
    };

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    const handleClose = () => {
        navigate(-1);
    };

    const MainForm = () => (
        <div>
            <EditUserMainBox
                viewData={viewData as UserEditProfileResponse}
                viewErrors={viewFieldsErrors}
                onFormUpdate={handleUpdateViewData}
                onFormValidation={(valid) =>
                    setBoxValidations((state) => {
                        return { ...state, mainBox: valid };
                    })
                }
                onError={e => setViewError(e)}
            />

            <EditUserSecondaryBox
                viewData={viewData as UserEditProfileResponse}
                viewErrors={viewFieldsErrors}
                phoneTypeItems={phoneTypes.current ?? []}
                onSubForm={(subform) => setSubForm(subform)}
                onFormUpdate={handleUpdateViewData}
                onFormValidation={(valid) =>
                    setBoxValidations((state) => {
                        return { ...state, tabsBox: valid };
                    })
                }
                onError={e => setViewError(e)}
            />

        </div>
    );

    // Ao carregar um DTO:
    useEffect(() => {
        async function getData() {
            try {

                let result = await api.v1.user.getEditProfile({ userId: props.userId });
                refreshData(result.data);
            } catch (e: any) {
                setViewError(e as Error);
            }
            setLoading(false);
        }

        if (loading) {
            if (phoneTypes.current === undefined) {
                api.v1.selectors.userPhoneTypes()
                    .then(results => {
                        phoneTypes.current = results.data;
                        getData();
                    });
            }
            else getData();
        }
    }, [loading]);

    // Ao salvar um DTO:
    useEffect(() => {
        async function postData() {
            try {
                var dto = getSaveCommand();
                var updatedUser = await api.v1.user.updateProfile(dto);
                setLoading(true);
                setReloadOnClose(true);
                if (props.userId == session.user.userId)
                    session.refreshUserProperties(updatedUser.data);
            } catch (e) {
                resolveErrors(e);
            }

            setSaving(false);
        }

        if (saving) postData();
    }, [saving]);

    // Ao editar o DTO atual:
    useEffect(() => {
        const isRecordChanged = !LibUtils.deepCompare(viewData, loadDto);
        setRecordChanged(isRecordChanged);
    }, [viewData]);

    return (
        <Box>
            <MprcRequestIndicator visible={saving} type={"post"} />
            <MprcRequestErrorDialog error={viewError} onClose={() => setViewError(undefined)} />
            {
                subForm ||
                <Box>

                    <MprcContainer position="sticky" margin="none" >
                        <EditUserHeader reloadOnClose={reloadOnClose} recordChanged={recordChanged} onClose={handleClose} actions={getHeaderItems()} />
                    </MprcContainer>

                    <MprcInfiniteLoading visible={loading} />

                    <Container maxWidth="lg">
                        {!loading && MainForm()}
                    </Container>
                </Box>
            }
        </Box>
    );
};
